/**
 * usePanelTransition — manages the mount/unmount lifecycle and CSS transition
 * state for SlidePanel.
 *
 * Returns { mounted, visible }:
 *  - mounted: whether the panel DOM (portal) should exist at all. Stays true
 *    during the slide-out transition so the exit animation completes.
 *  - visible: whether .tlx-panel--open is applied. Toggling this triggers the CSS
 *    transition.
 *
 * Also owns:
 *  - Scroll lock (blocks page scroll while the panel is open)
 *  - Focus restoration (returns focus to the trigger element after close,
 *    unless the user has already moved focus elsewhere)
 *
 * When the trigger no longer exists at close time - acting in the panel can
 * remove the button that opened it - focus falls back to the first focusable
 * element of the trigger's nearest `[data-panel-return-focus]` ancestor.
 * Opt-in, so a caller without that marker keeps the plain behaviour.
 */
import { useEffect, useRef, useState } from "react";
import { getFocusable } from "@/helpers/focus";
import { useScrollBlock } from "@/hooks/useScrollBlock";
import {
  SLIDE_PANEL_TRANSITION_MS,
  SLIDE_PANEL_UNMOUNT_BUFFER_MS,
} from "./constants";

/**
 * Focus went away with the panel: it fell back to `<body>` when the panel's
 * DOM was removed, or sits on a node that is no longer in the document.
 */
const focusWasLost = (): boolean => {
  const active = document.activeElement;
  return !active || active === document.body || !active.isConnected;
};

export function usePanelTransition(open: boolean): {
  mounted: boolean;
  visible: boolean;
} {
  const [mounted, setMounted] = useState(open);
  const [visible, setVisible] = useState(false);
  const triggerRef = useRef<Element | null>(null);
  const returnScopeRef = useRef<HTMLElement | null>(null);
  const [blockScroll, allowScroll] = useScrollBlock();

  useEffect(() => {
    if (open) {
      triggerRef.current = document.activeElement;
      returnScopeRef.current =
        document.activeElement?.closest<HTMLElement>(
          "[data-panel-return-focus]",
        ) ?? null;
      setMounted(true);
      // One frame delay lets the browser paint the off-screen initial state
      // so the CSS enter transition fires on the subsequent class add.
      const frame = requestAnimationFrame(() => setVisible(true));
      blockScroll();
      return () => cancelAnimationFrame(frame);
    } else {
      setVisible(false);
      allowScroll();
      const timer = setTimeout(
        () => setMounted(false),
        SLIDE_PANEL_TRANSITION_MS,
      );
      // Restore focus after the panel fully leaves the screen
      const trigger = triggerRef.current as HTMLElement | null;
      const returnScope = returnScopeRef.current;
      let focusTimer: ReturnType<typeof setTimeout> | undefined;
      if (trigger && typeof trigger.focus === "function") {
        focusTimer = setTimeout(() => {
          // Only return focus the panel took with it. If the user has already
          // moved focus elsewhere (clicked a search box while the panel slid
          // out), pulling it back would send their next keystrokes astray.
          if (focusWasLost()) {
            if (trigger.isConnected) trigger.focus();
            else if (returnScope?.isConnected)
              getFocusable(returnScope)[0]?.focus();
          }
          // Release the nodes so a removed trigger is not kept alive.
          triggerRef.current = null;
          returnScopeRef.current = null;
        }, SLIDE_PANEL_TRANSITION_MS + SLIDE_PANEL_UNMOUNT_BUFFER_MS);
      }
      return () => {
        clearTimeout(timer);
        clearTimeout(focusTimer);
      };
    }
  }, [open, blockScroll, allowScroll]);

  return { mounted, visible };
}
